import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as FamaLogo } from "../../assets/logo.svg";
import { useResetPassword } from "../../api/legacy/LegacyUserAPI";

function validatePassword(password: string): boolean {
  if (password.length < 8) return false;
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[^a-zA-Z0-9]/.test(password);
  return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
}

function LegacyPasswordReset() {
  const { token = "" } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saveMessage, setSaveMessage] = useState<React.ReactNode>("");
  const [saveFailed, setSaveFailed] = useState(false);
  const resetPasswordMutation = useResetPassword();

  const clearErrors = useCallback(() => {
    setSaveFailed(false);
    setSaveMessage("");
  }, []);

  const onSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault?.();
    clearErrors();
    resetPasswordMutation.mutate(
      { token, password },
      {
        onSuccess: () => {
          setSaveFailed(false);
          setSaveMessage(
            <>
              Your password has been successfully reset. You can now use your
              credentials to authenticate to the V1 API.
              <br />
              <br />
              See the developer documentation for more information:{" "}
              <a
                href="https://developer.fama.io/docs/access-token"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600"
              >
                https://developer.fama.io/docs/access-token
              </a>{" "}
            </>
          );
        },
        onError: (error: any) => {
          let message;
          if (
            error?.response?.status === 400 &&
            error?.response?.data?.message
          ) {
            message =
              "This password reset link is invalid or has expired.\n\nPlease request a new password reset link or contact support@fama.io for further assistance.";
          } else {
            message =
              "Error resetting password. Please try again later. If the problem persists, please contact support@fama.io.";
          }
          setSaveMessage(message);
          setSaveFailed(true);
        },
      }
    );
  };

  const savingPassword = resetPasswordMutation.isLoading;

  const passwordInvalid = password.length > 0 && !validatePassword(password);
  const passwordMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="p-14 mx-auto max-w-md bg-white mt-20 shadow-lg rounded-lg gap-12 flex flex-col">
      <div data-svg-wrapper className="relative flex justify-center mb-0">
        <FamaLogo className="w-48 h-auto" />
      </div>

      <div className="mt-10 text-gray-700 text-3xl font-bold font-['Noto Sans'] leading-9">
        Reset Your Password
      </div>

      <form onSubmit={onSubmit} noValidate autoComplete="off">
        <div className="mt-2">
          <label
            htmlFor="password"
            className="block text-gray-700 font-semibold mb-2 font-['Noto Sans']"
          >
            New Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="form-control border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full font-['Noto Sans']"
            placeholder="Enter New Password"
            name="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearErrors();
            }}
            required
          />
          {passwordInvalid && (
            <div className="mt-2 mb-2 px-4 py-3 bg-red-50 text-red-600 text-sm font-medium font-['Noto Sans'] rounded">
              Your password does not meet the requirements. Please ensure it
              includes at least 8 characters, one uppercase letter, one number,
              and one special character.
            </div>
          )}
        </div>
        <div className="mt-8">
          <label
            htmlFor="confirmPassword"
            className="block text-gray-700 font-semibold mb-2 font-['Noto Sans']"
          >
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            className="form-control border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full font-['Noto Sans']"
            placeholder="Confirm New Password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              clearErrors();
            }}
            required
          />
          {passwordMismatch && (
            <div className="mt-2 mb-2 px-4 py-3 bg-red-50 text-red-600 text-sm font-medium font-['Noto Sans'] rounded">
              Passwords do not match.
            </div>
          )}
        </div>
      </form>

      {saveMessage && (
        <div
          className={`mb-4 px-4 py-3 ${
            saveFailed ? "bg-red-100" : "bg-green-100"
          }`}
        >
          <div
            className={`text-sm font-medium font-['Noto Sans'] leading-tight ${
              saveFailed ? "text-red-600" : "text-green-600"
            } ${typeof saveMessage === "string" ? "whitespace-pre-line" : ""}`}
          >
            {saveMessage}
          </div>
        </div>
      )}

      {!saveMessage && (
        <div>
          <button
            type="button"
            aria-label="Reset Password"
            className="w-full [background-color:#46B276] text-white font-bold py-2 px-4 rounded hover:[background-color:#36895a] transition-colors disabled:opacity-50 font-['Noto Sans']"
            disabled={savingPassword || !password || !confirmPassword}
            onClick={onSubmit}
          >
            {savingPassword ? (
              <span className="animate-spin mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full align-middle" />
            ) : null}
            Reset Password
          </button>
        </div>
      )}

      <div className="mt-8 text-gray-700 text-sm font-semibold font-['Noto Sans'] leading-tight">
        <button
          type="button"
          className="hover:underline"
          onClick={() => (window.location.href = "/")}
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}

export default LegacyPasswordReset;
